package uz.ferro.shop.pages.admin.pickup

import js.core.jso
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import mui.material.Button
import mui.material.ButtonVariant
import mui.material.FormControl
import mui.material.FormControlVariant
import mui.material.InputLabel
import mui.material.MenuItem
import mui.material.Select
import mui.material.TextField
import mui.system.sx
import react.FC
import react.Props
import react.ReactNode
import react.dom.events.ChangeEvent
import react.dom.onChange
import react.router.useParams
import react.useContext
import react.useEffectOnce
import react.useState
import uz.ferro.shop.NavigationContext
import uz.ferro.shop.manager.PickupPointManager
import uz.ferro.shop.model.GeoAddress
import uz.ferro.shop.model.PickupPoint
import uz.ferro.shop.navigator.NavigationPage
import uz.ferro.shop.pages.admin.AlertManagerContext
import uz.ferro.shop.pages.admin.dict.DictEditor
import uz.ferro.shop.ui.AppText
import uz.ferro.shop.ui.HorizontalLayout
import uz.ferro.shop.ui.VerticalLayout
import web.cssom.JustifyContent
import web.cssom.number
import web.cssom.pct
import web.cssom.px
import web.html.HTMLInputElement

private val notDecimalRegex = Regex("[^0-9.]")

val AdminPickupPointEdit = FC<Props> {

    val pathParam = useParams()["pickupPointId"] ?: return@FC
    val pickupPointId = pathParam.toLongOrNull()
    var pickupPoint by useState<PickupPoint>(PickupPoint())
    var latitudeInput by useState("")
    var longitudeInput by useState("")
    val alertManager = useContext(AlertManagerContext)
    val navigator = useContext(NavigationContext)

    useEffectOnce {
        MainScope().launch {
            if (pickupPointId != null && pickupPoint.id != pickupPointId) {
                try {
                    val pp = PickupPointManager.getAdminPickupPoint(pickupPointId)
                    latitudeInput = pp.address?.latitude?.toString().orEmpty()
                    longitudeInput = pp.address?.longitude?.toString().orEmpty()
                    pickupPoint = pp
                } catch (e: Exception) {
                    // no-op
                }
            }
        }
    }

    VerticalLayout {
        sx {
            padding = 15.px
            height = 100.pct
        }

        VerticalLayout {
            space = 24.px
            sx {
                width = 500.px
                flexGrow = number(1.0)
            }
            DictEditor {
                title = "Nomi"
                item = pickupPoint.title
                onChange = {
                    pickupPoint = pickupPoint.copy(title = it)
                }
            }

            AppText {
                text = "Manzil"
            }

            DictEditor {
                title = "Umumiy manzil"
                item = pickupPoint.address?.addressLine
                onChange = {
                    pickupPoint = pickupPoint.copy(
                        address = (pickupPoint.address ?: GeoAddress()).copy(
                            addressLine = it
                        )
                    )
                }
            }

            DictEditor {
                title = "Ko'cha"
                item = pickupPoint.address?.street
                onChange = {
                    pickupPoint = pickupPoint.copy(
                        address = (pickupPoint.address ?: GeoAddress()).copy(
                            street = it
                        )
                    )
                }
            }

            TextField {
                label = ReactNode("Uy")
                variant = FormControlVariant.outlined
                value = pickupPoint.address?.house.orEmpty()
                InputLabelProps = jso {
                    shrink = true
                }
                onChange = { event ->
                    event as ChangeEvent<HTMLInputElement>
                    pickupPoint = pickupPoint.copy(
                        address = (pickupPoint.address ?: GeoAddress()).copy(
                            house = event.target.value,
                        )
                    )
                }
            }

            TextField {
                label = ReactNode("Xonadon")
                variant = FormControlVariant.outlined
                value = pickupPoint.address?.apartment.orEmpty()
                InputLabelProps = jso {
                    shrink = true
                }
                onChange = { event ->
                    event as ChangeEvent<HTMLInputElement>
                    pickupPoint = pickupPoint.copy(
                        address = (pickupPoint.address ?: GeoAddress()).copy(
                            apartment = event.target.value,
                        )
                    )
                }
            }

            TextField {
                label = ReactNode("Shimoliy kenglik (41.2355 kabi)")
                variant = FormControlVariant.outlined
                value = latitudeInput
                InputLabelProps = jso {
                    shrink = true
                }
                onChange = { event ->
                    event as ChangeEvent<HTMLInputElement>
                    val text = event.target.value
                        .replace(notDecimalRegex, "")
                    latitudeInput = text
                }
            }

            TextField {
                label = ReactNode("Sharqiy uzunlik (69.3435 kabi)")
                variant = FormControlVariant.outlined
                InputLabelProps = jso {
                    shrink = true
                }
                value = longitudeInput
                onChange = { event ->
                    event as ChangeEvent<HTMLInputElement>
                    val text = event.target.value
                        .replace(notDecimalRegex, "")
                    longitudeInput = text
                }
            }

            FormControl {
                fullWidth = true
                InputLabel {
                    +"Holat"
                }
                Select {
                    value = if (pickupPoint.disabled == true) "disabled" else "enabled"
                    label = ReactNode("Holat")
                    onChange = { evt, _ ->
                        val status = evt.target.value
                        pickupPoint = pickupPoint.copy(disabled = status == "disabled")
                    }

                    MenuItem {
                        value = "enabled"
                        +"Aktiv"
                    }

                    MenuItem {
                        value = "disabled"
                        +"Noaktiv"
                    }
                }
            }
        }

        fun saveData() {
            var pp = pickupPoint

            if (latitudeInput.isNotBlank() != longitudeInput.isNotBlank()) {
                alertManager.showError("Koordinatalarni to'g'ri kiriting")
                return
            }

            var latitude: Double? = null
            var longitude: Double? = null

            if (latitudeInput.isNotBlank() && longitudeInput.isNotBlank()) {
                latitude = latitudeInput.toDoubleOrNull()
                longitude = longitudeInput.toDoubleOrNull()
                if (latitude == null || longitude == null) {
                    alertManager.showError("Koordinatalarni to'g'ri kiriting")
                    return
                }
            }

            pp = pp.copy(
                address = (pickupPoint.address ?: GeoAddress()).copy(
                    latitude = latitude,
                    longitude = longitude
                )
            )

            MainScope().launch {
                try {
                    val result = PickupPointManager.savePickupPoint(pp)
                    pickupPoint = result
                    if (pickupPointId == null) {
                        navigator.openPage(NavigationPage.ADMIN_PICKUP_POINT, result.id!!)
                    }
                    alertManager.showSuccess("Saqlandi")
                } catch (e: Exception) {
                    alertManager.handleError(e)
                }
            }
        }

        HorizontalLayout {
            space = 24.px
            sx {
                justifyContent = JustifyContent.end
            }

            Button {
                variant = ButtonVariant.contained
                onClick = {
                    saveData()
                }
                +"Saqlash"
            }
        }
    }
}